import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Modal, ModalFooter } from '@/components/ui/modal';
import {
  launchConfigService,
  LaunchConfig,
  LaunchConfigUpdate,
} from '@/services/launch-config.service';
import { toast } from 'react-hot-toast';
import { Save, X, Edit, Clock } from 'lucide-react';
import { ConfigField } from './ui/ConfigField';
import { LoadingState } from './ui/LoadingState';
import { ErrorState } from './ui/ErrorState';
import { useLaunchConfigForm } from './hooks/useLaunchConfigForm';
import { formatDateWithPaddedDay } from '@/utils/helpers';
import { CONFIG_FIELDS } from './constants';
import { useGlobalRefresh } from '@/contexts/GlobalRefreshContext';
import { useDataChange } from '@/contexts/DataChangeContext';

function formatDateTime(dateString: string | undefined) {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString();
}

const ClientPage = () => {
  const queryClient = useQueryClient();
  const {
    data: config,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useQuery<LaunchConfig>({
    queryKey: ['launch-config', 'current'],
    queryFn: () => launchConfigService.getCurrentConfig(),
    retry: 1,
    retryDelay: 1000,
  });

  const [editing, setEditing] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const { refreshing } = useGlobalRefresh();
  const { ignoreNextChange } = useDataChange();

  const {
    editForm,
    errors,
    isDirty,
    updateField,
    resetForm,
    validate,
    getUpdatePayload,
  } = useLaunchConfigForm(config);

  // Warn on tab/browser close if there are unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const updateConfigMutation = useMutation({
    mutationFn: (data: LaunchConfigUpdate) =>
      launchConfigService.updateConfig(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(['launch-config', 'current'], updated);
      if (updated?.valuation_date) {
        queryClient.setQueryData(['valuation-date'], updated.valuation_date);
      } else {
        queryClient.invalidateQueries({ queryKey: ['valuation-date'] });
      }
      toast.success('Configuration updated successfully');
      setEditing(false);
      ignoreNextChange('Client', 10000); // Ignore changes for 10 seconds after user's own action
    },
    onError: (error) => {
      console.error('Error updating configuration:', error);
      toast.error('Failed to update configuration');
    },
  });

  const handleSaveEdit = () => {
    if (!validate()) return;
    updateConfigMutation.mutate(getUpdatePayload() as LaunchConfigUpdate);
  };

  const handleCancelEdit = () => {
    resetForm();
    setEditing(false);
  };

  if (loading) {
    return <LoadingState />;
  }

  if (isError || !config) {
    console.error('Error loading launch config:', error);
    return <ErrorState onRetry={() => refetch()} />;
  }

  return (
    <div className="bg-gray-50" data-test-id="launch-config-page-container">
      <div className="page-inner" data-test-id="launch-config-page-inner">
        {/* Header */}
        <div className="mb-4" data-test-id="header-section">
          <div
            className="flex justify-between items-center"
            data-test-id="header-wrapper"
          >
            <div data-test-id="header-content">
              <h1 className="text-2xl font-bold mb-2" data-test-id="page-title">
                Client Information
              </h1>
            </div>
            <div className="flex gap-2" data-test-id="header-actions"></div>
          </div>
        </div>

        {/* Client Configuration Form */}
        <Card
          className="px-6 pb-6 pt-6 mb-6 bg-blue-50 border-blue-200 relative"
          data-test-id="client-config-card"
        >
          <div className="absolute top-6 right-6">
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                disabled={refreshing}
                className="text-blue-600 hover:text-blue-900 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Edit className="h-4 w-4" />
                <span className="text-sm">Edit</span>
              </button>
            ) : (
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveEdit}
                  disabled={refreshing}
                  size="sm"
                  className="flex items-center gap-1"
                >
                  <Save className="h-4 w-4" />
                  <span>Save</span>
                </Button>
                <Button
                  onClick={() =>
                    isDirty ? setShowLeaveConfirm(true) : handleCancelEdit()
                  }
                  disabled={refreshing}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </Button>
              </div>
            )}
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-6 gap-y-6"
            style={{ gridAutoRows: 'min-content' }}
            data-test-id="client-config-grid"
          >
            {CONFIG_FIELDS.map((field) => {
              const configValue = config?.[field.key];
              const getDisplayValue = () => {
                if (editing) return undefined;
                if (field.type === 'date' && configValue) {
                  return formatDateWithPaddedDay(configValue as string);
                }
                if (field.key === 'mark_frequency') {
                  return configValue || '—';
                }
                return configValue || '—';
              };

              return (
                <ConfigField
                  key={field.key}
                  label={field.label}
                  value={
                    field.key === 'mark_frequency'
                      ? editForm[field.key] || '__none__'
                      : editForm[field.key] || ''
                  }
                  editing={editing}
                  error={errors[field.key] || ''}
                  required={field.required}
                  type={field.type}
                  placeholder={field.placeholder || ''}
                  onChange={(value) => {
                    const fieldKey = field.key as keyof LaunchConfigUpdate;
                    if (field.key === 'mark_frequency') {
                      updateField(
                        fieldKey,
                        value === '__none__' ? null : (value as string)
                      );
                    } else {
                      updateField(fieldKey, value);
                    }
                  }}
                  selectOptions={field.selectOptions || []}
                  displayValue={getDisplayValue()}
                />
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-blue-200">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <Clock className="h-4 w-4" />
              <span>
                Last updated: {formatDateTime(config?.updated_at) || '—'}
              </span>
            </div>
          </div>
        </Card>

        {/* Leave confirmation */}
        <Modal
          isOpen={showLeaveConfirm}
          onClose={() => setShowLeaveConfirm(false)}
          title="Discard changes?"
          description="You have unsaved changes. Do you want to discard them?"
        >
          <ModalFooter>
            <Button
              variant="outline"
              onClick={() => setShowLeaveConfirm(false)}
            >
              Continue editing
            </Button>
            <Button
              onClick={() => {
                setShowLeaveConfirm(false);
                handleCancelEdit();
              }}
            >
              Discard
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default ClientPage;
